import {
  Badge,
  Box,
  Button,
  Center,
  Checkbox,
  Flex,
  Heading,
  HStack,
  Input,
  Spacer,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tag,
  Text,
  VStack,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { createColumnHelper } from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { startOfMonth } from "date-fns";
import { DateRange } from "react-day-picker";
import {
  ApprovalStatus,
  CurrencyCode,
  Transaction,
} from "../../../API";
import { DataTable } from "../../../components/data-table";
import DateRangeSelector from "../../../components/date-range-selector";
import { MultiSelect, MultiSelectOption } from "../../../components/select";
import StatusChip from "../../../components/status-chip";
import { formatAmount, formatDate, getBookkeepingYear, getLocalDate, queryList } from "../../../utils";
import { useApprovalInbox } from "../../../hooks/approvals/useApprovalInbox";
import { useApproveTransaction } from "../../../hooks/approvals/useApproveTransaction";
import { useRejectTransaction } from "../../../hooks/approvals/useRejectTransaction";
import { useRequestChanges } from "../../../hooks/approvals/useRequestChanges";
import { useContextTypeApprovals } from "../dashboard-layout";
import ApprovalDetailDrawer from "./approval-detail-drawer";
import { listUsers } from "../../../graphql/queries";
import { ListUsersQuery, ListUsersQueryVariables } from "../../../API";

type UserLookup = { id: string; firstName: string; lastName: string };

/** Enriched transaction with display fields derived from lookups */
type ApprovalTransaction = Transaction & {
  userName: string;
  bookingAccountName: string;
};

const columnHelper = createColumnHelper<ApprovalTransaction>();

const Approvals = () => {
  const toast = useToast();
  const { user, selectedOrganization, selectedBookkeepingFull } =
    useContextTypeApprovals();
  const [tabIndex, setTabIndex] = useState(0);

  // Derive bookkeepingYearId from selected bookkeeping
  const bookkeepingYear = useMemo(
    () =>
      selectedBookkeepingFull
        ? getBookkeepingYear(selectedBookkeepingFull)
        : undefined,
    [selectedBookkeepingFull]
  );
  const bookkeepingYearId = bookkeepingYear?.id;

  // Fetch inbox data
  const { data: inboxData, isLoading } = useApprovalInbox(bookkeepingYearId);

  // Mutation hooks
  const approveTransaction = useApproveTransaction(bookkeepingYearId);
  const rejectTransaction = useRejectTransaction(bookkeepingYearId);
  const requestChanges = useRequestChanges(bookkeepingYearId);

  // Fetch users for name lookup
  const [usersMap, setUsersMap] = useState<Map<string, UserLookup>>(new Map());
  useEffect(() => {
    if (!selectedBookkeepingFull?.userBookkeepings?.length) return;
    const userIds = selectedBookkeepingFull.userBookkeepings.map(
      (userBookkeeping) => userBookkeeping.userId
    );
    let cancelled = false;
    queryList<UserLookup, ListUsersQueryVariables, ListUsersQuery>(listUsers, {
      filter: { or: userIds.map((id) => ({ id: { eq: id } })) },
    }).then((users) => {
      if (cancelled) return;
      const map = new Map<string, UserLookup>();
      users.forEach((u) => map.set(u.id, u));
      setUsersMap(map);
    });
    return () => {
      cancelled = true;
    };
  }, [selectedBookkeepingFull]);

  // Build bookingAccount name lookup from bookkeeping context
  const bookingAccountNameMap = useMemo(() => {
    const map = new Map<string, string>();
    selectedBookkeepingFull?.bookingAccounts?.forEach((bookingAccount) => {
      map.set(bookingAccount.id, bookingAccount.name);
    });
    return map;
  }, [selectedBookkeepingFull]);

  // Enrich transactions with display fields
  const enrichTransaction = useCallback(
    (transaction: Transaction): ApprovalTransaction => {
      const lookup = usersMap.get(transaction.userId);
      return {
        ...transaction,
        userName: lookup
          ? `${lookup.lastName} ${lookup.firstName.substring(0, 1)}.`
          : transaction.userId,
        bookingAccountName:
          bookingAccountNameMap.get(transaction.bookingAccountId) ?? "-",
      };
    },
    [usersMap, bookingAccountNameMap]
  );

  // Split inbox into tabs
  const { pending, changesRequested, history } = useMemo(() => {
    const all = (inboxData ?? []).map(enrichTransaction);
    return {
      pending: all.filter(
        (transaction) =>
          transaction.approvalStatus === ApprovalStatus.pending
      ),
      changesRequested: all.filter(
        (transaction) =>
          transaction.approvalStatus === ApprovalStatus.changesRequested
      ),
      // History is fetched from the same data for now (approved/rejected come from inbox query)
      // For a full history view, a separate query could be added later
      history: all.filter(
        (transaction) =>
          transaction.approvalStatus === ApprovalStatus.approved ||
          transaction.approvalStatus === ApprovalStatus.rejected
      ),
    };
  }, [inboxData, enrichTransaction]);

  // Selection state for bulk approve
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Detail drawer state — selectedTransaction stays mounted across close so
  // the slide-out animation plays (matches transactions.tsx pattern).
  const [selectedTransaction, setSelectedTransaction] =
    useState<ApprovalTransaction | null>(null);
  const {
    isOpen: isDrawerOpen,
    onOpen: onDrawerOpen,
    onClose: onDrawerClose,
  } = useDisclosure();

  // Filter state
  const [filterEmployee, setFilterEmployee] = useState<MultiSelectOption[]>([]);
  const [filterCategory, setFilterCategory] = useState<MultiSelectOption[]>([]);
  const [filterAmountMin, setFilterAmountMin] = useState("");
  const [filterAmountMax, setFilterAmountMax] = useState("");
  const [filterDateRange, setFilterDateRange] = useState<
    DateRange | undefined
  >(() => {
    const today = new Date();
    return { from: getLocalDate(startOfMonth(today)), to: getLocalDate(today) };
  });

  // Derive filter options from all transactions
  const allTransactions = useMemo(
    () => [...pending, ...changesRequested, ...history],
    [pending, changesRequested, history]
  );
  const employeeOptions: MultiSelectOption[] = useMemo(() => {
    const unique = new Map<string, string>();
    allTransactions.forEach((transaction) =>
      unique.set(transaction.userId, transaction.userName)
    );
    return Array.from(unique.entries()).map(([id, name]) => ({
      value: id,
      label: name,
    }));
  }, [allTransactions]);
  const categoryOptions: MultiSelectOption[] = useMemo(() => {
    const unique = new Set<string>();
    allTransactions.forEach((transaction) =>
      unique.add(transaction.bookingAccountName)
    );
    return Array.from(unique)
      .sort()
      .map((name) => ({ value: name, label: name }));
  }, [allTransactions]);

  const hasActiveFilters =
    filterEmployee.length > 0 ||
    filterCategory.length > 0 ||
    !!filterAmountMin ||
    !!filterAmountMax;

  const applyFilters = useCallback(
    (transactions: ApprovalTransaction[]) => {
      return transactions.filter((transaction) => {
        if (
          filterEmployee.length > 0 &&
          !filterEmployee.some(
            (employee) => employee.value === transaction.userId
          )
        )
          return false;
        if (
          filterCategory.length > 0 &&
          !filterCategory.some(
            (category) =>
              category.value === transaction.bookingAccountName
          )
        )
          return false;
        if (
          filterAmountMin &&
          transaction.amount < parseFloat(filterAmountMin)
        )
          return false;
        if (
          filterAmountMax &&
          transaction.amount > parseFloat(filterAmountMax)
        )
          return false;
        if (filterDateRange?.from) {
          const paidDate = new Date(transaction.paidAt);
          if (paidDate < filterDateRange.from) return false;
        }
        if (filterDateRange?.to) {
          const paidDate = new Date(transaction.paidAt);
          const toEnd = new Date(filterDateRange.to);
          toEnd.setHours(23, 59, 59, 999);
          if (paidDate > toEnd) return false;
        }
        return true;
      });
    },
    [
      filterEmployee,
      filterCategory,
      filterAmountMin,
      filterAmountMax,
      filterDateRange,
    ]
  );

  const filteredPending = useMemo(
    () => applyFilters(pending),
    [applyFilters, pending]
  );
  const filteredChangesRequested = useMemo(
    () => applyFilters(changesRequested),
    [applyFilters, changesRequested]
  );
  const filteredHistory = useMemo(
    () => applyFilters(history),
    [applyFilters, history]
  );

  const clearFilters = () => {
    setFilterEmployee([]);
    setFilterCategory([]);
    setFilterAmountMin("");
    setFilterAmountMax("");
    const today = new Date();
    setFilterDateRange({
      from: getLocalDate(startOfMonth(today)),
      to: getLocalDate(today),
    });
  };

  // Build auth groups from the current context
  const authGroups = useMemo(() => {
    if (!selectedOrganization?.id || !selectedBookkeepingFull?.id) return [];
    return [
      `${selectedOrganization.id}-admin`,
      `${selectedOrganization.id}-bk-${selectedBookkeepingFull.id}-head`,
      `${selectedOrganization.id}-bk-${selectedBookkeepingFull.id}-employee`,
    ];
  }, [selectedOrganization, selectedBookkeepingFull]);

  // Auto-advance: after an action, swap drawer to next pending transaction or close if none remain
  const advanceToNext = useCallback(
    (currentId: string, list: ApprovalTransaction[]) => {
      const currentIndex = list.findIndex(
        (transaction) => transaction.id === currentId
      );
      const remaining = list.filter(
        (transaction) => transaction.id !== currentId
      );
      if (remaining.length === 0) {
        onDrawerClose();
        toast({
          title: "Alle erledigt",
          status: "success",
          duration: 3000,
        });
      } else {
        const nextIndex = Math.min(currentIndex, remaining.length - 1);
        setSelectedTransaction(remaining[nextIndex]);
      }
    },
    [toast, onDrawerClose]
  );

  const handleApprove = useCallback(
    (transactionId: string) => {
      const transaction = pending.find(
        (transaction) => transaction.id === transactionId
      );
      if (!transaction || !user) return;

      approveTransaction.mutate({
        transactionId,
        reviewerUserId: user.id,
        authGroups,
        authGroupsRead: authGroups,
      });

      toast({
        title: "Genehmigt",
        description: `${transaction.title} wurde genehmigt.`,
        status: "success",
        duration: 3000,
      });

      if (selectedTransaction?.id === transactionId) {
        advanceToNext(transactionId, filteredPending);
      }
    },
    [
      pending,
      user,
      authGroups,
      approveTransaction,
      toast,
      selectedTransaction,
      advanceToNext,
      filteredPending,
    ]
  );

  const handleReject = useCallback(
    (transactionId: string, comment: string) => {
      const transaction = pending.find(
        (transaction) => transaction.id === transactionId
      );
      if (!transaction || !user) return;

      rejectTransaction.mutate({
        transactionId,
        reviewerUserId: user.id,
        comment,
        authGroups,
        authGroupsRead: authGroups,
      });

      toast({
        title: "Abgelehnt",
        description: `${transaction.title} wurde abgelehnt.`,
        status: "error",
        duration: 3000,
      });

      if (selectedTransaction?.id === transactionId) {
        advanceToNext(transactionId, filteredPending);
      }
    },
    [
      pending,
      user,
      authGroups,
      rejectTransaction,
      toast,
      selectedTransaction,
      advanceToNext,
      filteredPending,
    ]
  );

  const handleRequestChanges = useCallback(
    (transactionId: string, comment: string) => {
      const transaction = pending.find(
        (transaction) => transaction.id === transactionId
      );
      if (!transaction || !user) return;

      requestChanges.mutate({
        transactionId,
        reviewerUserId: user.id,
        comment,
        authGroups,
        authGroupsRead: authGroups,
      });

      toast({
        title: "Änderungen angefragt",
        description: `Änderungen für ${transaction.title} wurden angefragt.`,
        status: "warning",
        duration: 3000,
      });

      if (selectedTransaction?.id === transactionId) {
        advanceToNext(transactionId, filteredPending);
      }
    },
    [
      pending,
      user,
      authGroups,
      requestChanges,
      toast,
      selectedTransaction,
      advanceToNext,
      filteredPending,
    ]
  );

  const handleBulkApprove = async () => {
    const toApprove = pending.filter((transaction) =>
      selectedIds.has(transaction.id)
    );
    if (toApprove.length === 0 || !user) return;

    // Parallel: each mutation gets its own optimistic snapshot (no serial drift)
    const results = await Promise.allSettled(
      toApprove.map((transaction) =>
        approveTransaction
          .mutateAsync({
            transactionId: transaction.id,
            reviewerUserId: user.id,
            authGroups,
            authGroupsRead: authGroups,
          })
          .then(() => transaction.id)
      )
    );

    const succeededIds = new Set(
      results
        .filter(
          (result): result is PromiseFulfilledResult<string> =>
            result.status === "fulfilled"
        )
        .map((result) => result.value)
    );
    const failedCount = toApprove.length - succeededIds.size;

    // Keep failed items selected so the user can retry them
    setSelectedIds(
      (previous) =>
        new Set([...previous].filter((id) => !succeededIds.has(id)))
    );

    if (failedCount === 0) {
      toast({
        title: `${succeededIds.size} Transaktionen genehmigt`,
        status: "success",
        duration: 3000,
      });
    } else if (succeededIds.size === 0) {
      toast({
        title: "Fehler beim Genehmigen",
        description: `Keine der ${toApprove.length} Transaktionen konnte genehmigt werden.`,
        status: "error",
        duration: 5000,
      });
    } else {
      toast({
        title: `${succeededIds.size} von ${toApprove.length} Transaktionen genehmigt`,
        description: `${failedCount} fehlgeschlagen. Bitte erneut versuchen.`,
        status: "warning",
        duration: 5000,
      });
    }
  };

  const toggleSelection = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = useCallback(
    (transactions: ApprovalTransaction[]) => {
      const allIds = transactions.map((transaction) => transaction.id);
      setSelectedIds((prev) => {
        const allSelected = allIds.every((id) => prev.has(id));
        return allSelected ? new Set() : new Set(allIds);
      });
    },
    []
  );

  const bulkApproveEnabled = selectedIds.size > 0;

  // Keyboard shortcuts when drawer is open
  useEffect(() => {
    if (!selectedTransaction || !isDrawerOpen) return;
    const handler = (event: KeyboardEvent) => {
      if (
        event.target instanceof HTMLTextAreaElement ||
        event.target instanceof HTMLInputElement
      )
        return;

      if (
        event.key.toLowerCase() === "g" &&
        selectedTransaction.approvalStatus === ApprovalStatus.pending
      ) {
        event.preventDefault();
        handleApprove(selectedTransaction.id);
      }

      if (event.key === "ArrowUp" || event.key === "ArrowDown") {
        event.preventDefault();
        const currentList =
          tabIndex === 0
            ? filteredPending
            : tabIndex === 1
            ? filteredChangesRequested
            : filteredHistory;
        const currentIdx = currentList.findIndex(
          (transaction) => transaction.id === selectedTransaction.id
        );
        if (currentIdx === -1) return;
        const nextIdx =
          event.key === "ArrowUp"
            ? Math.max(0, currentIdx - 1)
            : Math.min(currentList.length - 1, currentIdx + 1);
        if (nextIdx !== currentIdx) {
          setSelectedTransaction(currentList[nextIdx]);
        }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [
    selectedTransaction,
    isDrawerOpen,
    handleApprove,
    tabIndex,
    filteredPending,
    filteredChangesRequested,
    filteredHistory,
  ]);

  const baseColumns = useMemo(
    () => [
      columnHelper.accessor("paidAt", {
        header: "Datum",
        cell: (info) => (
          <Text fontSize="sm" whiteSpace="nowrap">
            {formatDate(new Date(info.getValue()))}
          </Text>
        ),
      }),
      columnHelper.accessor("title", {
        header: "Transaktion",
        cell: (info) => (
          <Text
            fontSize="sm"
            maxWidth="240px"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {info.getValue()}
          </Text>
        ),
      }),
      columnHelper.accessor("amount", {
        header: "Betrag",
        cell: (info) => {
          const row = info.row.original;
          return (
            <Text fontSize="sm" whiteSpace="nowrap">
              {formatAmount(info.getValue(), row.currencyCode)}
            </Text>
          );
        },
        meta: { isNumeric: true },
      }),
      columnHelper.accessor("baseCurrencyAmount", {
        header: "CHF Betrag",
        cell: (info) => {
          const value = info.getValue();
          return value ? (
            <Text fontSize="sm" whiteSpace="nowrap">
              {formatAmount(value, CurrencyCode.chf)}
            </Text>
          ) : null;
        },
        meta: { isNumeric: true },
      }),
      columnHelper.accessor("bookingAccountName", {
        header: "Buchungskategorie",
        cell: (info) => (
          <Tag size="sm" colorScheme="primary" variant="subtle">
            {info.getValue()}
          </Tag>
        ),
      }),
      columnHelper.accessor("userName", {
        header: "Mitarbeiter",
        cell: (info) => (
          <Text fontSize="sm" whiteSpace="nowrap">
            {info.getValue()}
          </Text>
        ),
      }),
    ],
    []
  );

  const pendingColumns = useMemo(
    () => [
      columnHelper.display({
        id: "select",
        header: () => (
          <Box onClick={(event) => event.stopPropagation()}>
            <Checkbox
              isChecked={
                filteredPending.length > 0 &&
                filteredPending.every((transaction) =>
                  selectedIds.has(transaction.id)
                )
              }
              isIndeterminate={
                filteredPending.some((transaction) =>
                  selectedIds.has(transaction.id)
                ) &&
                !filteredPending.every((transaction) =>
                  selectedIds.has(transaction.id)
                )
              }
              onChange={() => toggleSelectAll(filteredPending)}
              colorScheme="primary"
            />
          </Box>
        ),
        cell: (info) => (
          <Box onClick={(event) => event.stopPropagation()}>
            <Checkbox
              isChecked={selectedIds.has(info.row.original.id)}
              onChange={() => toggleSelection(info.row.original.id)}
              colorScheme="primary"
            />
          </Box>
        ),
      }),
      ...baseColumns,
    ],
    [baseColumns, filteredPending, selectedIds, toggleSelectAll]
  );

  const historyColumns = useMemo(
    () => [
      ...baseColumns,
      columnHelper.accessor("approvalStatus", {
        header: "Entscheidung",
        cell: (info) => {
          const status = info.getValue();
          if (!status) return null;
          // StatusChip returns null for notRequired internally
          return <StatusChip type="approval" status={status} />;
        },
      }),
    ],
    [baseColumns]
  );

  const changesColumns = useMemo(() => [...baseColumns], [baseColumns]);

  if (isLoading) {
    return (
      <Center p="8">
        <Spinner size="lg" color="primary.500" />
      </Center>
    );
  }

  return (
    <VStack p="4" alignItems="start" width="full">
      <Flex
        alignItems={{ base: "stretch", md: "center" }}
        gap={{ base: "2", md: "4" }}
        width="full"
        zIndex={2}
        direction={{ base: "column", md: "row" }}
      >
        <Heading size="md">Freigaben</Heading>
        <Spacer />
        <MultiSelect
          options={employeeOptions}
          value={filterEmployee}
          onChange={setFilterEmployee}
          placeholder="Mitarbeiter..."
          allLabel="Alle Mitarbeiter"
        />
        <MultiSelect
          options={categoryOptions}
          value={filterCategory}
          onChange={setFilterCategory}
          placeholder="Kategorien..."
          allLabel="Alle Kategorien"
        />
        <Input
          type="number"
          placeholder="Betrag von"
          width="120px"
          value={filterAmountMin}
          onChange={(event) => setFilterAmountMin(event.target.value)}
        />
        <Input
          type="number"
          placeholder="Betrag bis"
          width="120px"
          value={filterAmountMax}
          onChange={(event) => setFilterAmountMax(event.target.value)}
        />
        <DateRangeSelector
          state="success"
          range={filterDateRange}
          setRange={setFilterDateRange}
        />
        {hasActiveFilters && (
          <Button
            size="sm"
            variant="ghost"
            onClick={clearFilters}
            whiteSpace="nowrap"
          >
            Filter zurücksetzen
          </Button>
        )}
      </Flex>
      <Tabs
        width="full"
        variant="soft-rounded"
        colorScheme="primary"
        index={tabIndex}
        onChange={(index) => {
          setTabIndex(index);
          setSelectedIds(new Set());
        }}
      >
        <Flex alignItems="center" gap="2">
          {tabIndex === 0 && bulkApproveEnabled && (
            <HStack spacing="2" mr="2">
              <Text fontSize="sm" color="gray.600" whiteSpace="nowrap">
                {selectedIds.size} ausgewählt
              </Text>
              <Button
                size="sm"
                colorScheme="primary"
                onClick={handleBulkApprove}
              >
                Genehmigen
              </Button>
            </HStack>
          )}
          <TabList>
            <Tab>
              Ausstehend
              {filteredPending.length > 0 && (
                <Badge ml="2" colorScheme="warning" borderRadius="full">
                  {filteredPending.length}
                </Badge>
              )}
            </Tab>
            <Tab>
              Änderungen angefragt
              {filteredChangesRequested.length > 0 && (
                <Badge ml="2" colorScheme="warning" borderRadius="full">
                  {filteredChangesRequested.length}
                </Badge>
              )}
            </Tab>
            <Tab>Verlauf</Tab>
          </TabList>
        </Flex>

        <TabPanels>
          <TabPanel px={0}>
            <DataTable
              typeNamePlural="Freigaben"
              columns={pendingColumns}
              data={filteredPending}
              state={isLoading ? "loading" : "success"}
              setSelectedIndex={(index) => {
                if (typeof index === "number" && filteredPending[index]) {
                  setSelectedTransaction(filteredPending[index]);
                  onDrawerOpen();
                }
              }}
            />
          </TabPanel>

          <TabPanel px={0}>
            <DataTable
              typeNamePlural="Freigaben"
              columns={changesColumns}
              data={filteredChangesRequested}
              state={isLoading ? "loading" : "success"}
              setSelectedIndex={(index) => {
                if (
                  typeof index === "number" &&
                  filteredChangesRequested[index]
                ) {
                  setSelectedTransaction(filteredChangesRequested[index]);
                  onDrawerOpen();
                }
              }}
            />
          </TabPanel>

          <TabPanel px={0}>
            <DataTable
              typeNamePlural="Freigaben"
              columns={historyColumns}
              data={filteredHistory}
              state={isLoading ? "loading" : "success"}
              setSelectedIndex={(index) => {
                if (typeof index === "number" && filteredHistory[index]) {
                  setSelectedTransaction(filteredHistory[index]);
                  onDrawerOpen();
                }
              }}
            />
          </TabPanel>
        </TabPanels>
      </Tabs>

      {selectedTransaction && (
        <ApprovalDetailDrawer
          transaction={selectedTransaction}
          isOpen={isDrawerOpen}
          onClose={onDrawerClose}
          onApprove={handleApprove}
          onReject={handleReject}
          onRequestChanges={handleRequestChanges}
          isPending={
            selectedTransaction.approvalStatus === ApprovalStatus.pending
          }
        />
      )}
    </VStack>
  );
};

export default Approvals;
